import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import SEOHead from '../../components/SEO/SEOHead';

const PricingPageArabic = () => {
    const { i18n } = useTranslation();
    const [showTrialModal, setShowTrialModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    // Set language to Arabic when component mounts
    useEffect(() => {
        if (i18n.language !== 'ar') {
            i18n.changeLanguage('ar');
        }
    }, [i18n]);
    const [trialForm, setTrialForm] = useState({
        name: '',
        phone: ''
    });

    const handleTrialSubmit = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (!trialForm.name.trim() || !trialForm.phone.trim()) {
            toast.error('يرجى ملء جميع الحقول المطلوبة.');
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            console.log('Submitting form data:', { name: trialForm.name, phone: trialForm.phone });
            
            const response = await fetch('https://formspree.io/f/mdkglkeg', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    name: trialForm.name.trim(),
                    phone: trialForm.phone.trim(),
                    message: 'طلب تجربة مجانية من صفحة الأسعار',
                    _subject: `طلب تجربة مجانية جديد من ${trialForm.name}`,
                    _captcha: false
                })
            });

            const responseData = await response.json();
            console.log('Response:', response.status, responseData);

            if (response.ok) {
                setIsSuccess(true);
                toast.success('تم إرسال طلب التجربة المجانية بنجاح! سنتواصل معك خلال 24 ساعة.');
                setTrialForm({ name: '', phone: '' });
                // Keep modal open to show success message
                setTimeout(() => {
                    setShowTrialModal(false);
                    setIsSuccess(false);
                }, 3000);
            } else {
                throw new Error(responseData.error || 'Failed to send request');
            }
        } catch (error) {
            console.error('Form submission error:', error);
            toast.error('حدث خطأ أثناء إرسال طلبك. يرجى المحاولة مرة أخرى.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <SEOHead 
                title="خطط الأسعار - برنامج إدارة الأسطول المملكة العربية السعودية"
                description="أسعار شفافة لبرنامج إدارة الأسطول لوجستيات. يبدأ من 2,990 ريال/سنوياً. تجربة مجانية 30 يوم، بدون رسوم إعداد. مثالي لشركات النقل السعودية."
                keywords="أسعار إدارة الأسطول، برنامج لوجستيات السعودية، تكلفة نظام النقل، إدارة الشاحنات السعودية، برنامج النقل المملكة"
                canonicalUrl="/ar/pricing"
                lang="ar"
                structuredData={{
                    "@context": "https://schema.org",
                    "@type": "Product",
                    "name": "برنامج إدارة الأسطول لوجستيات",
                    "description": "نظام إدارة أسطول متكامل لشركات النقل في المملكة العربية السعودية",
                    "brand": {
                        "@type": "Brand",
                        "name": "لوجستيات"
                    },
                    "offers": [
                        {
                            "@type": "Offer",
                            "name": "باقة المبتدئ",
                            "price": "2990",
                            "priceCurrency": "SAR",
                            "priceValidUntil": "2026-12-31",
                            "availability": "https://schema.org/InStock",
                            "description": "مثالي للأساطيل الصغيرة حتى 5 مركبات",
                            "url": "https://logistiat.app/ar/pricing",
                            "itemCondition": "https://schema.org/NewCondition",
                            "shippingDetails": {
                                "@type": "OfferShippingDetails",
                                "shippingRate": {
                                    "@type": "MonetaryAmount",
                                    "value": "0",
                                    "currency": "SAR"
                                },
                                "shippingDestination": {
                                    "@type": "DefinedRegion",
                                    "addressCountry": "SA"
                                },
                                "deliveryTime": {
                                    "@type": "ShippingDeliveryTime",
                                    "handlingTime": {
                                        "@type": "QuantitativeValue",
                                        "minValue": 0,
                                        "maxValue": 0,
                                        "unitCode": "DAY"
                                    },
                                    "transitTime": {
                                        "@type": "QuantitativeValue",
                                        "minValue": 0,
                                        "maxValue": 0,
                                        "unitCode": "DAY"
                                    }
                                }
                            },
                            "hasMerchantReturnPolicy": {
                                "@type": "MerchantReturnPolicy",
                                "applicableCountry": "SA",
                                "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                                "merchantReturnDays": 30,
                                "returnMethod": "https://schema.org/ReturnByMail",
                                "returnFees": "https://schema.org/FreeReturn"
                            }
                        },
                        {
                            "@type": "Offer",
                            "name": "باقة المحترف",
                            "price": "4200",
                            "priceCurrency": "SAR",
                            "priceValidUntil": "2026-12-31",
                            "availability": "https://schema.org/InStock",
                            "description": "الأفضل للأساطيل النامية حتى 25 مركبة",
                            "url": "https://logistiat.app/ar/pricing",
                            "itemCondition": "https://schema.org/NewCondition",
                            "shippingDetails": {
                                "@type": "OfferShippingDetails",
                                "shippingRate": {
                                    "@type": "MonetaryAmount",
                                    "value": "0",
                                    "currency": "SAR"
                                },
                                "shippingDestination": {
                                    "@type": "DefinedRegion",
                                    "addressCountry": "SA"
                                },
                                "deliveryTime": {
                                    "@type": "ShippingDeliveryTime",
                                    "handlingTime": {
                                        "@type": "QuantitativeValue",
                                        "minValue": 0,
                                        "maxValue": 0,
                                        "unitCode": "DAY"
                                    },
                                    "transitTime": {
                                        "@type": "QuantitativeValue",
                                        "minValue": 0,
                                        "maxValue": 0,
                                        "unitCode": "DAY"
                                    }
                                }
                            },
                            "hasMerchantReturnPolicy": {
                                "@type": "MerchantReturnPolicy",
                                "applicableCountry": "SA",
                                "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                                "merchantReturnDays": 30,
                                "returnMethod": "https://schema.org/ReturnByMail",
                                "returnFees": "https://schema.org/FreeReturn"
                            }
                        }
                    ]
                }}
            />
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
                {/* Header */}
                <header className="bg-white shadow-sm border-b">
                    <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex justify-between items-center py-6">
                            <div className="flex items-center">
                                <Link to="/ar" className="text-2xl font-bold text-gray-900">Logistiat</Link>
                            </div>
                            <div className="hidden md:flex space-x-8 space-x-reverse">
                                <Link to="/ar/features" className="text-gray-600 hover:text-gray-900 transition-colors">المميزات</Link>
                                <Link to="/ar/pricing" className="text-blue-600 font-medium">الأسعار</Link>
                                <Link to="/ar/trip-costing" className="text-gray-600 hover:text-gray-900 transition-colors">تكلفة الرحلات</Link>
                                <Link to="/ar/contact" className="text-gray-600 hover:text-gray-900 transition-colors">اتصل بنا</Link>
                                <Link to="/en" className="text-gray-600 hover:text-gray-900 transition-colors">English</Link>
                            </div>
                            <div className="flex space-x-4 space-x-reverse">
                                <Link
                                    to="/login"
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                                >
                                    تسجيل الدخول
                                </Link>
                            </div>
                        </div>
                    </nav>
                </header>

                {/* Hero Section */}
                <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            أسعار بسيطة وشفافة
                        </h1>
                        <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
                            اختر الباقة المثالية لحجم أسطولك. جميع الباقات تشمل المميزات الأساسية مع تجربة مجانية لمدة 30 يوماً. وفر حتى 20% مع الفوترة السنوية.
                        </p>
                        <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 text-blue-100">
                            <CheckCircleIcon className="w-5 h-5 ml-2" />
                            الأسعار السنوية المعروضة - وفر شهرين مقارنة بالشهرية
                        </div>
                    </div>
                </section>

                {/* Pricing Plans */}
                <section className="py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-3 gap-8">
                            {/* Starter Plan */}
                            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">المبتدئ</h3>
                                    <p className="text-gray-600 mb-4">مثالي للأساطيل الصغيرة</p>
                                    <div className="text-4xl font-bold text-gray-900 mb-2">2,990 ريال</div>
                                    <p className="text-gray-600">سنوياً</p>
                                    <p className="text-sm text-green-600 font-medium mt-2">وفر 600 ريال مقابل الشهرية • حتى 5 مركبات</p>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        حتى 5 مركبات
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        شحنات غير محدودة
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        احتساب تكلفة أساسي
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        إدارة السائقين
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        تقارير أساسية
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        دعم عبر البريد الإلكتروني
                                    </li>
                                </ul>

                                <button
                                    onClick={() => setShowTrialModal(true)}
                                    className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                                >
                                    ابدأ التجربة المجانية
                                </button>
                            </div>

                            {/* Professional Plan */}
                            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-blue-500 relative">
                                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-semibold">الأكثر شعبية</span>
                                </div>

                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">المحترف</h3>
                                    <p className="text-gray-600 mb-4">الأفضل للأساطيل النامية</p>
                                    <div className="text-4xl font-bold text-gray-900 mb-2">4,200 ريال</div>
                                    <p className="text-gray-600">سنوياً</p>
                                    <p className="text-sm text-green-600 font-medium mt-2">وفر 1,200 ريال مقابل الشهرية • حتى 25 مركبة</p>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        حتى 25 مركبة
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        كل ما في باقة المبتدئ
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        احتساب تكلفة متقدم
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        تكامل بترو آب للوقود
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        إدارة ضريبة القيمة المضافة
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        تحليلات متقدمة
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        دعم أولوية
                                    </li>
                                </ul>

                                <button
                                    onClick={() => setShowTrialModal(true)}
                                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                                >
                                    ابدأ التجربة المجانية
                                </button>
                            </div>

                            {/* Enterprise Plan */}
                            <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">المؤسسات</h3>
                                    <p className="text-gray-600 mb-4">للأساطيل الكبيرة</p>
                                    <div className="text-4xl font-bold text-gray-900 mb-2">مخصص</div>
                                    <p className="text-gray-600">التسعير</p>
                                    <p className="text-sm text-gray-500 mt-2">مركبات غير محدودة</p>
                                </div>

                                <ul className="space-y-4 mb-8">
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        مركبات غير محدودة
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        كل ما في باقة المحترف
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        تكاملات مخصصة
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        دعم متعدد الشركات
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        أدوار مستخدمين متقدمة
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        وصول API
                                    </li>
                                    <li className="flex items-center">
                                        <svg className="w-5 h-5 text-green-500 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        دعم مخصص
                                    </li>
                                </ul>

                                <Link
                                    to="/ar/contact"
                                    className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-center block"
                                >
                                    اتصل بالمبيعات
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section className="py-20 bg-gray-50">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                                الأسئلة الشائعة
                            </h2>
                        </div>

                        <div className="space-y-8">
                            <div className="bg-white p-6 rounded-lg shadow-sm">
                                <h3 className="text-xl font-semibold mb-3">هل هناك تجربة مجانية؟</h3>
                                <p className="text-gray-600">نعم، جميع الخطط تأتي مع تجربة مجانية لمدة 30 يوماً. لا حاجة لبطاقة ائتمان للبدء.</p>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow-sm">
                                <h3 className="text-xl font-semibold mb-3">هل يمكنني تغيير الباقة في أي وقت؟</h3>
                                <p className="text-gray-600">بالطبع! يمكنك ترقية أو تخفيض باقتك في أي وقت. التغييرات تسري فوراً.</p>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow-sm">
                                <h3 className="text-xl font-semibold mb-3">ما طرق الدفع المقبولة؟</h3>
                                <p className="text-gray-600">نقبل جميع البطاقات الائتمانية الرئيسية، التحويلات البنكية، ويمكننا ترتيب الفوترة لعملاء المؤسسات.</p>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow-sm">
                                <h3 className="text-xl font-semibold mb-3">هل بياناتي آمنة؟</h3>
                                <p className="text-gray-600">نعم، نستخدم أمان على مستوى المؤسسات مع تشفير البيانات، النسخ الاحتياطية المنتظمة، ونلتزم بأنظمة حماية البيانات السعودية.</p>
                            </div>

                            <div className="bg-white p-6 rounded-lg shadow-sm">
                                <h3 className="text-xl font-semibold mb-3">هل تقدمون التدريب والدعم؟</h3>
                                <p className="text-gray-600">نعم، نقدم إعداد شامل، مواد تدريبية، ودعم مستمر باللغتين العربية والإنجليزية.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-20 bg-blue-600 text-white">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">
                            مستعد للبدء؟
                        </h2>
                        <p className="text-xl mb-8">
                            انضم لمئات شركات النقل السعودية التي تستخدم لوجستيات لإدارة أساطيلها بكفاءة أكبر.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => setShowTrialModal(true)}
                                className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                            >
                                ابدأ التجربة المجانية
                            </button>
                            <Link
                                to="/ar/contact"
                                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors"
                            >
                                اتصل بالمبيعات
                            </Link>
                        </div>
                    </div>
                </section>

                {/* Footer */}
                <footer className="bg-gray-900 text-white py-12">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-4 gap-8">
                            <div>
                                <h3 className="text-xl font-bold mb-4">Logistiat</h3>
                                <p className="text-gray-400">
                                    حل إدارة الأسطول الشامل لشركات النقل في المملكة العربية السعودية.
                                </p>
                            </div>
                            <div>
                                <h4 className="font-semibold mb-4">المميزات</h4>
                                <ul className="space-y-2 text-gray-400">
                                    <li><Link to="/ar/features" className="hover:text-white">إدارة الأسطول</Link></li>
                                    <li><Link to="/ar/trip-costing" className="hover:text-white">تكلفة الرحلات</Link></li>
                                    <li><Link to="/ar/features" className="hover:text-white">إدارة السائقين</Link></li>
                                    <li><Link to="/ar/features" className="hover:text-white">تتبع الوقود</Link></li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-semibold mb-4">الشركة</h4>
                                <ul className="space-y-2 text-gray-400">
                                    <li><Link to="/ar/contact" className="hover:text-white">اتصل بنا</Link></li>
                                    <li><Link to="/ar/pricing" className="hover:text-white">الأسعار</Link></li>
                                    <li><Link to="/en" className="hover:text-white">English</Link></li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="font-semibold mb-4">الدعم</h4>
                                <ul className="space-y-2 text-gray-400">
                                    <li><Link to="/ar/contact" className="hover:text-white">مركز المساعدة</Link></li>
                                    <li><Link to="/ar/contact" className="hover:text-white">اتصل بالدعم</Link></li>
                                </ul>
                            </div>
                        </div>
                        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
                            <p>&copy; 2024 Logistiat. جميع الحقوق محفوظة.</p>
                        </div>
                    </div>
                </footer>
            </div>

            {/* Trial Modal */}
            {showTrialModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-900">
                                ابدأ تجربتك المجانية
                            </h3>
                            <button
                                onClick={() => setShowTrialModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                        
                        {isSuccess ? (
                            <div className="text-center py-8">
                                <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                <h4 className="text-xl font-semibold text-gray-900 mb-2">تم بنجاح!</h4>
                                <p className="text-gray-600 mb-4">
                                    تم إرسال طلب التجربة المجانية بنجاح!
                                </p>
                                <p className="text-sm text-gray-500">
                                    سنتواصل معك خلال 24 ساعة لإعداد حسابك.
                                </p>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-600 mb-6">
                                    احصل على وصول كامل لـ Logistiat لمدة 14 يوماً. لا حاجة لبطاقة ائتمان.
                                </p>
                        
                                <form onSubmit={handleTrialSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                                    الاسم الكامل *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={trialForm.name}
                                    onChange={(e) => setTrialForm({...trialForm, name: e.target.value})}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-right"
                                    placeholder="الاسم الكامل"

                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
                                    رقم الهاتف *
                                </label>
                                <input
                                    type="tel"
                                    required
                                    value={trialForm.phone}
                                    onChange={(e) => setTrialForm({...trialForm, phone: e.target.value})}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                                    placeholder="+966 50 000 0000"

                                />
                            </div>
                            
                            <div className="flex gap-3 flex-row-reverse">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? 'جاري الإرسال...' : 'ابدأ التجربة'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowTrialModal(false)}
                                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                                >
                                    إلغاء
                                </button>
                            </div>
                                </form>
                        
                                <p className="text-sm text-gray-500 mt-4 text-center">
                                    سنتواصل معك خلال 24 ساعة لإعداد حسابك
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default PricingPageArabic;